// Firestore service – real implementations for Milestone 2

import {
  getFirestore,
  collection,
  doc,
  getDoc,
  getDocs,
  getDocsFromServer,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  type DocumentData,
  type QueryConstraint,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { firebaseApp } from './firebase.config';
import { isDemoMode } from '../../demo/demoDetection';
import { assertNotDemo } from '../../demo/demoFirestoreGuard';
import {
  demoGetCollection,
  demoGetDocument,
  demoGetDocumentByUserId,
} from '../../demo/demoFirestoreRouter';

const db = getFirestore(firebaseApp);
const auth = getAuth(firebaseApp);

/**
 * Deep-strip undefined values from any object/array before Firestore writes.
 * Firestore rejects undefined anywhere in the payload — this prevents silent write failures.
 */
function sanitizeForFirestore<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map(sanitizeForFirestore) as T;
  }
  if (value !== null && typeof value === 'object' && !(value instanceof Date)) {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [k, sanitizeForFirestore(v)])
    ) as T;
  }
  return value;
}

// Debug helper to check auth state
function logAuthState() {
  const currentUser = auth.currentUser;
  return currentUser;
}

export type FirestoreDocument<T> = T & { id: string };

/**
 * Get a single document by path
 */
export async function getDocument<T>(
  path: string,
): Promise<FirestoreDocument<T> | null> {
  // Demo mode: route to demo data
  if (isDemoMode()) {
    const result = demoGetDocument<FirestoreDocument<T>>(path);
    return result ?? null;
  }

  const docRef = doc(db, path);
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) {
    return null;
  }
  
  return {
    id: docSnap.id,
    ...(docSnap.data() as T),
  } as FirestoreDocument<T>;
}

/**
 * Get a collection with optional query constraints
 * BUG FIX: Now uses getDocsFromServer to bypass Firestore's local cache
 * This ensures we always get fresh data after updates/deletes
 */
export async function getCollection<T>(
  path: string,
  ...constraints: QueryConstraint[]
): Promise<FirestoreDocument<T>[]>;
export async function getCollection<T>(
  path: string,
  caller: string,
  ...constraints: QueryConstraint[]
): Promise<FirestoreDocument<T>[]>;
export async function getCollection<T>(
  path: string,
  callerOrConstraint?: string | QueryConstraint,
  ...rest: QueryConstraint[]
): Promise<FirestoreDocument<T>[]> {
  // Overload resolution: if first extra arg is a string it's a caller label
  let caller: string | undefined;
  let constraints: QueryConstraint[];
  if (typeof callerOrConstraint === 'string') {
    caller = callerOrConstraint;
    constraints = rest;
  } else {
    caller = undefined;
    constraints = callerOrConstraint ? [callerOrConstraint, ...rest] : rest;
  }

  if (import.meta.env.DEV) {
    console.log('[Firestore] *** getCollection ***', path, caller ? `caller=${caller}` : '', 'isDemoMode=', isDemoMode());
  }
  // Demo mode: route to demo data with constraint filtering
  if (isDemoMode()) {
    const result = demoGetCollection<FirestoreDocument<T>>(path, constraints);
    return result ?? [];
  }

  const collectionRef = collection(db, path);
  const q = constraints.length > 0 ? query(collectionRef, ...constraints) : collectionRef;
  
  // BUG FIX: Use getDocsFromServer to bypass local cache
  // This prevents ghost calories where Firestore returns stale cached data after edits
  try {
    const querySnapshot = await getDocsFromServer(q);
    return querySnapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...(docSnap.data() as T),
    })) as FirestoreDocument<T>[];
  } catch (error) {
    // Fallback to cached read if server read fails (e.g., offline)
    console.warn('[Firestore] getCollection: Server read failed, falling back to cache:', error);
    const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...(docSnap.data() as T),
    })) as FirestoreDocument<T>[];
  }
}

/**
 * Add a document to a collection
 */
export async function addDocument<T extends DocumentData>(
  path: string,
  data: T,
): Promise<FirestoreDocument<T>> {
  assertNotDemo('addDocument');
  // Check Firebase Auth state
  const currentUser = logAuthState();
  
  // CRITICAL: Check if data.userId matches auth.currentUser.uid
  const dataUserId = (data as { userId?: string }).userId;
  if (currentUser && dataUserId && dataUserId !== currentUser.uid) {
    console.error('[Firestore] ⚠️ WARNING: userId mismatch! This will cause permission denied!');
  }
  
  try {
    const collectionRef = collection(db, path);
    const docRef = await addDoc(collectionRef, sanitizeForFirestore(data));
    
    return {
      id: docRef.id,
      ...data,
    } as FirestoreDocument<T>;
  } catch (error) {
    console.error('[Firestore] addDocument failed:', error);
    if (error && typeof error === 'object' && 'code' in error) {
      console.error('[Firestore] Firebase error code:', (error as { code: string }).code);
    }
    throw error;
  }
}

/**
 * Set a document with a specific ID (upsert — creates or overwrites).
 * Use this when you want the document ID to be a known key (e.g. Firebase Auth UID).
 */
export async function setDocumentWithId<T extends DocumentData>(
  collectionPath: string,
  docId: string,
  data: T,
  options?: { merge?: boolean },
): Promise<FirestoreDocument<T>> {
  const currentUser = logAuthState();
  const dataUserId = (data as { userId?: string }).userId;
  if (currentUser && dataUserId && dataUserId !== currentUser.uid) {
    console.error('[Firestore] ⚠️ WARNING: userId mismatch! This will cause permission denied!');
  }

  try {
    const docRef = doc(db, collectionPath, docId);
    await setDoc(docRef, sanitizeForFirestore(data), options ?? {});
    return { id: docId, ...data } as FirestoreDocument<T>;
  } catch (error) {
    console.error('[Firestore] setDocumentWithId failed:', error);
    if (error && typeof error === 'object' && 'code' in error) {
      console.error('[Firestore] Firebase error code:', (error as { code: string }).code);
    }
    throw error;
  }
}

/**
 * Update a document
 */
export async function updateDocument<T extends DocumentData>(
  path: string,
  data: Partial<T>,
): Promise<void> {
  assertNotDemo('updateDocument');
  const docRef = doc(db, path);
  await updateDoc(docRef, sanitizeForFirestore(data) as DocumentData);
}

/**
 * Delete a document
 */
export async function deleteDocument(path: string): Promise<void> {
  assertNotDemo('deleteDocument');
  const docRef = doc(db, path);
  await deleteDoc(docRef);
}

/**
 * Get a document by user ID (single document per user).
 * Tries direct doc(userId) lookup first (fast + cheap),
 * falls back to query on userId field for legacy auto-ID docs.
 */
export async function getDocumentByUserId<T>(
  collectionPath: string,
  userId: string,
): Promise<FirestoreDocument<T> | null> {
  // Demo mode: route to demo data
  if (isDemoMode()) {
    console.log('[Firestore] *** getDocumentByUserId DEMO HIT ***', collectionPath);
    const result = demoGetDocumentByUserId<FirestoreDocument<T>>(collectionPath);
    return result ?? null;
  }

  // Fast path: doc keyed by UID
  // Wrapped in try/catch — if the collection uses userId field (not UID as doc ID),
  // the get rule may deny access when the doc doesn't exist (resource is null).
  // In that case fall through to the query path.
  try {
    const directRef = doc(db, collectionPath, userId);
    const directSnap = await getDoc(directRef);
    if (directSnap.exists()) {
      return { id: directSnap.id, ...(directSnap.data() as T) } as FirestoreDocument<T>;
    }
  } catch (error) {
    const code = (error as any)?.code;
    if (code !== 'permission-denied') throw error;
    // permission-denied on direct get — doc uses auto-ID, fall through to query
  }

  // Legacy fallback: query by userId field
  // Catch permission-denied errors — collection may be empty (deleted doc) or rules block list
  try {
    const results = await getCollection<T>(
      collectionPath,
      where('userId', '==', userId),
      limit(1),
    );
    if (results.length > 0) {
      if (import.meta.env.DEV) {
        console.log(`[Firestore] ${collectionPath}: resolved ${userId} via legacy query — migrate to doc(uid)`);
      }
      return results[0];
    }
  } catch (error) {
    const code = (error as any)?.code;
    if (code === 'permission-denied') {
      // Empty collection or rules block list — not an error, just no doc yet
      return null;
    }
    throw error;
  }

  return null;
}

/**
 * Get documents by user ID and date range
 */
export async function getDocumentsByUserAndDateRange<T>(
  collectionPath: string,
  userId: string,
  startDate: string, // YYYY-MM-DD
  endDate: string, // YYYY-MM-DD
): Promise<FirestoreDocument<T>[]> {
  return getCollection<T>(
    collectionPath,
    where('userId', '==', userId),
    where('date', '>=', startDate),
    where('date', '<=', endDate),
    orderBy('date', 'asc'),
    orderBy('createdAt', 'desc'),
  );
}

// Legacy mock functions for backward compatibility (can be removed later)
export type MockDocument<T> = FirestoreDocument<T>;

export async function mockGetCollection<T>(
  path: string,
): Promise<MockDocument<T>[]> {
  return getCollection<T>(path);
}

export async function mockAddDocument<T>(
  path: string,
  data: T,
): Promise<MockDocument<T>> {
  return addDocument(path, data as DocumentData) as Promise<MockDocument<T>>;
}

export async function mockUpdateDocument<T>(
  path: string,
  data: Partial<T>,
): Promise<Partial<T>> {
  await updateDocument(path, data as DocumentData);
  return data;
}


